import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Landing Page
import LandingPageLegacy from "@/pages/LandingPageLegacy";  // archived — kept for emergency rollback only
import LandingHome from "@/pages/landing/LandingHome";
import LandingStudent from "@/pages/landing/LandingStudent";
import LandingParent from "@/pages/landing/LandingParent";
import LandingSchool from "@/pages/landing/LandingSchool";

// Entry & Role Selection
import AppInit from "@/pages/AppInit";

// Auth Flows
import ParentAuth from "@/pages/ParentAuth";
import StudentAuth from "@/pages/StudentAuth";
import AuthCallbackNew from "@/pages/AuthCallbackNew";
import ParentOnboardingCreateChild from "@/pages/ParentOnboardingCreateChild";
import StudentOnboarding from "@/pages/StudentOnboarding";

// Main Landings
import FamilyHome from "@/pages/FamilyHome";
import SetPinScreen from "@/pages/SetPinScreen";
import AdultReloginGate from "@/pages/AdultReloginGate";
import AdultAuthChoice from "@/pages/AdultAuthChoice";

// Child Journey
import ChildHomeComplete from "@/pages/ChildHomeComplete";

// Parent Assignment Workflow
import ParentDashboard from "@/pages/ParentDashboard";
import ParentChatView from "@/pages/ParentChatView";

// Chat
import ChatHome from "@/pages/ChatHome";
const SocraticChat = lazy(() => import("@/pages/SocraticChat"));

// Analytics
const ParentAnalytics = lazy(() => import("@/pages/ParentAnalytics"));

// Platform Admin
import PlatformAdmin from "@/pages/PlatformAdmin";
import PlatformAdminUsers from "@/pages/PlatformAdminUsers";
import PlatformAdminAuditLog from "@/pages/PlatformAdminAuditLog";
import PlatformAdminRoute from "@/components/PlatformAdminRoute";

// School Admin (Ticket 1A.4)
import SchoolAdminHome from "@/pages/SchoolAdminHome";
import SchoolAdminRoute from "@/components/SchoolAdminRoute";

// School Teacher (Ticket 1A.6α + 1A.6β + 1A.6γ)
import SchoolTeacherHome from "@/pages/SchoolTeacherHome";
import SchoolTeacherRoute from "@/components/SchoolTeacherRoute";
import SchoolTeacherRosterView from "@/pages/SchoolTeacherRosterView";
import SchoolTeacherChatView from "@/pages/SchoolTeacherChatView";
// Hidden Jun 2026 — pending fixes to subject-bucket resolution & dead chips
// import SchoolTeacherStudentAnalytics from "@/pages/SchoolTeacherStudentAnalytics";
import SchoolTeacherClassInsights from "@/pages/SchoolTeacherClassInsights";

// Assessment Module (Epic 1B)
import SchoolTeacherAssessments from "@/pages/SchoolTeacherAssessments";
import SchoolTeacherAssessmentNew from "@/pages/SchoolTeacherAssessmentNew";
import SchoolTeacherAssessmentReview from "@/pages/SchoolTeacherAssessmentReview";
import SchoolTeacherAssessmentStatus from "@/pages/SchoolTeacherAssessmentStatus";
import SchoolTeacherAssessmentAnalytics from "@/pages/SchoolTeacherAssessmentAnalytics";
import SchoolTeacherAttemptReview from "@/pages/SchoolTeacherAttemptReview";
import StudentAssessments from "@/pages/StudentAssessments";
import StudentAssessmentAttempt from "@/pages/StudentAssessmentAttempt";
import StudentAssessmentResult from "@/pages/StudentAssessmentResult";
import StudentMissions from "@/pages/student/StudentMissions";
import ParentChildMissions from "@/pages/parent/ParentChildMissions";

// School Parent (Ticket 1A.7)
import SchoolParentHome from "@/pages/SchoolParentHome";
import SchoolParentRoute from "@/components/SchoolParentRoute";
import SchoolParentChildAssessments from "@/pages/SchoolParentChildAssessments";

// School Admin Roster (Ticket 1A.7)
import RosterPage from "@/pages/schoolAdmin/RosterPage";

// School-branded login (Epic E)
import SchoolLogin from "@/pages/SchoolLogin";

// School Admin invite accept (Ticket 1A.4)
import InviteAccept from "@/pages/InviteAccept";

// Protected Route Component
import ProtectedRoute from "@/components/ProtectedRoute";

// Global Widgets
import HelpWidget from "@/components/HelpWidget";

function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment or query params (iOS fallback)
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallbackNew />;
  }

  return (
    <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#0f0a1a'}}><div style={{width:36,height:36,border:'3px solid rgba(255,255,255,0.1)',borderTopColor:'#a78bfa',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}>
    <Routes>
      {/* Landing Page — New marketing surface (Feb 2026 cut-over).
          `LandingPageLegacy` retained at /legacy-landing for emergency rollback
          until 2 weeks of stable production; then delete. */}
      <Route path="/" element={<LandingHome />} />
      <Route path="/student" element={<LandingStudent />} />
      <Route path="/parent" element={<LandingParent />} />
      <Route path="/school" element={<LandingSchool />} />
      {/* Backward-compat: prior /landing-v2 staging URLs continue to work */}
      <Route path="/landing-v2" element={<LandingHome />} />
      <Route path="/landing-v2/student" element={<LandingStudent />} />
      <Route path="/landing-v2/parent" element={<LandingParent />} />
      <Route path="/landing-v2/school" element={<LandingSchool />} />
      {/* Legacy landing page — rollback safety */}
      <Route path="/legacy-landing" element={<LandingPageLegacy />} />

      {/* Legacy App Entry (redirects to landing) */}
      <Route path="/app" element={<AppInit />} />

      {/* Role Selection */}
      <Route path="/role-select" element={<Navigate to="/" replace />} />

      {/* Auth Flows */}
      <Route path="/auth/parent" element={<ParentAuth />} />
      <Route path="/auth/student" element={<StudentAuth />} />
      <Route path="/auth/callback/parent" element={<AuthCallbackNew />} />
      <Route path="/auth/callback/parent/" element={<AuthCallbackNew />} />
      <Route path="/auth/callback/student" element={<AuthCallbackNew />} />
      <Route path="/auth/callback/student/" element={<AuthCallbackNew />} />

      {/* Parent Onboarding */}
      <Route path="/set-pin" element={<ProtectedRoute><SetPinScreen /></ProtectedRoute>} />
      <Route path="/parent/onboarding/create-child" element={<ProtectedRoute><ParentOnboardingCreateChild /></ProtectedRoute>} />

      {/* Student Onboarding */}
      <Route path="/student/onboarding" element={<ProtectedRoute><StudentOnboarding /></ProtectedRoute>} />

      {/* Main Landings */}
      <Route path="/family" element={<ProtectedRoute><FamilyHome /></ProtectedRoute>} />
      <Route path="/kids-landing" element={<ProtectedRoute><FamilyHome /></ProtectedRoute>} />
      <Route path="/adult/relogin-gate" element={<AdultReloginGate />} />
      <Route path="/adult/auth-choice" element={<AdultAuthChoice />} />

      {/* Child Journey */}
      <Route path="/child/:childId/home" element={<ChildHomeComplete />} />
      <Route path="/child/:childId/worlds" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/library-item/:itemId" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/world/:worldId" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/ask" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/badges" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/daily-content" element={<Navigate to="/family" replace />} />
      <Route path="/child/:childId/chat" element={<ChatHome />} />
      <Route path="/child/:childId/chat/:conversationId" element={<SocraticChat />} />
      <Route path="/expedition/:expeditionId" element={<Navigate to="/family" replace />} />
      <Route path="/expedition/:expeditionId/complete" element={<Navigate to="/family" replace />} />

      {/* Parent Dashboard */}
      <Route path="/parent/home" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/assign/:childId" element={<Navigate to="/parent/dashboard" replace />} />
      <Route path="/parent/drafts" element={<Navigate to="/parent/dashboard" replace />} />
      <Route path="/parent/drafts/:draftId" element={<Navigate to="/parent/dashboard" replace />} />
      <Route path="/parent/child/:childId/expeditions" element={<Navigate to="/parent/dashboard" replace />} />
      <Route path="/parent/child/:childId/chats" element={<ProtectedRoute><ParentChatView /></ProtectedRoute>} />
      <Route path="/parent/child/:childId/chats/:conversationId" element={<ProtectedRoute><ParentChatView /></ProtectedRoute>} />
      <Route path="/add-child" element={<ProtectedRoute><ParentOnboardingCreateChild /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/insights/:childId" element={<ProtectedRoute><ParentAnalytics /></ProtectedRoute>} />

      {/* School Teacher Dashboard (Ticket 1A.6α) */}
      <Route path="/school-teacher" element={<SchoolTeacherRoute><SchoolTeacherHome /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/class/:classId" element={<SchoolTeacherRoute><SchoolTeacherRosterView /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/class/:classId/insights" element={<SchoolTeacherRoute><SchoolTeacherClassInsights /></SchoolTeacherRoute>} />
      {/* Teacher chat + analytics views (Ticket 1A.6β) */}
      <Route path="/school-teacher/students/:enrollmentId/chats" element={<SchoolTeacherRoute><SchoolTeacherChatView /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/students/:enrollmentId/chats/:conversationId" element={<SchoolTeacherRoute><SchoolTeacherChatView /></SchoolTeacherRoute>} />
      {/* Hidden Jun 2026 — per-student Analytics surface has multiple known
          issues (bogus subject buckets, dead chip strip, broken totals).
          Re-enable after fixing analytics.py subject resolution. */}
      {/* <Route path="/school-teacher/students/:enrollmentId/analytics" element={<SchoolTeacherRoute><SchoolTeacherStudentAnalytics /></SchoolTeacherRoute>} /> */}
      {/* Assessment Module (Epic 1B) — teacher routes */}
      <Route path="/school-teacher/assessments" element={<SchoolTeacherRoute><SchoolTeacherAssessments /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/assessments/new" element={<SchoolTeacherRoute><SchoolTeacherAssessmentNew /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/assessments/:aid" element={<SchoolTeacherRoute><SchoolTeacherAssessmentReview /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/assessments/:aid/status" element={<SchoolTeacherRoute><SchoolTeacherAssessmentStatus /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/assessments/:aid/analytics" element={<SchoolTeacherRoute><SchoolTeacherAssessmentAnalytics /></SchoolTeacherRoute>} />
      <Route path="/school-teacher/attempts/:tid" element={<SchoolTeacherRoute><SchoolTeacherAttemptReview /></SchoolTeacherRoute>} />
      {/* Assessment Module (Epic 1B) — student routes */}
      <Route path="/student/assessments" element={<ProtectedRoute><StudentAssessments /></ProtectedRoute>} />
      <Route path="/student/assessments/:aid/attempt" element={<ProtectedRoute><StudentAssessmentAttempt /></ProtectedRoute>} />
      <Route path="/student/results/:tid" element={<ProtectedRoute><StudentAssessmentResult /></ProtectedRoute>} />
      <Route path="/student/missions" element={<ProtectedRoute><StudentMissions /></ProtectedRoute>} />
      {/* Legacy redirects (issued invite emails / older sessions) */}
      <Route path="/teacher/home" element={<Navigate to="/school-teacher" replace />} />
      <Route path="/teacher/class/:classId" element={<Navigate to="/school-teacher" replace />} />
      <Route path="/teacher/class/:classId/assign" element={<Navigate to="/school-teacher" replace />} />
      <Route path="/teacher/class/:classId/insights" element={<Navigate to="/school-teacher" replace />} />

      {/* School Parent Observer (Ticket 1A.7) */}
      <Route path="/school-parent" element={<SchoolParentRoute><SchoolParentHome /></SchoolParentRoute>} />
      <Route path="/school-parent/child/:cid/assessments" element={<SchoolParentRoute><SchoolParentChildAssessments /></SchoolParentRoute>} />
      <Route path="/parent/children/:cid/missions" element={<ProtectedRoute><ParentChildMissions /></ProtectedRoute>} />

      {/* Platform Admin (role-gated) */}
      <Route path="/platform-admin" element={<PlatformAdminRoute><PlatformAdmin /></PlatformAdminRoute>} />
      <Route path="/platform-admin/schools/:school_id/users" element={<PlatformAdminRoute><PlatformAdminUsers /></PlatformAdminRoute>} />
      <Route path="/platform-admin/audit-log" element={<PlatformAdminRoute><PlatformAdminAuditLog /></PlatformAdminRoute>} />

      {/* School Admin (role-gated) */}
      <Route path="/school-admin" element={<SchoolAdminRoute><SchoolAdminHome /></SchoolAdminRoute>} />
      <Route path="/school-admin/classes/:classId/roster" element={<SchoolAdminRoute><RosterPage /></SchoolAdminRoute>} />

      {/* School-branded login (invite-gated Email OTP) */}
      <Route path="/school/:slug/login" element={<SchoolLogin />} />

      {/* School Admin invite accept (public) */}
      <Route path="/invites/accept/:token" element={<InviteAccept />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <HelpWidget />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
